import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Chip,
	Divider,
	IconButton,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { ExecuteCodeListImportCommandResult } from "Infrastructure/Api/Api";

interface ImportProgressProps {
	result: ExecuteCodeListImportCommandResult | null;
}

const extractTitle = (text: string): string => {
	const firstLine = text.split("\n")[0];
	if (firstLine.length > 80) {
		return `${firstLine.slice(0, 80)}...`;
	}
	return firstLine;
};

const formatDuration = (duration: string): string => {
	const parts = duration.split(":");
	if (parts.length < 3) return duration;

	const hours = Number.parseInt(parts[0], 10);
	const minutes = Number.parseInt(parts[1], 10);
	const secondsWithMs = parts[2];
	const seconds = Math.round(Number.parseFloat(secondsWithMs));

	const result: string[] = [];
	if (hours > 0) result.push(`${hours}h`);
	if (minutes > 0 || hours > 0) result.push(`${minutes}m`);
	result.push(`${seconds}s`);

	return result.join(" ");
};

const handleCopyToClipboard = async (text: string) => {
	try {
		await navigator.clipboard.writeText(text);
	} catch (err) {
		console.error("Failed to copy to clipboard:", err);
	}
};

export const ImportProgress: React.FunctionComponent<ImportProgressProps> = ({
	result,
}) => {
	if (!result) {
		return null;
	}

	const hasIssues = result.errors.length > 0 || result.warnings.length > 0;
	const getStatusMessage = () => {
		if (result.success && !hasIssues) {
			return "Import byl úspěšně dokončen.";
		}
		if (result.success && hasIssues) {
			return "Import dokončen s varováními.";
		}
		if (!result.success && result.definitionsProcessed > 0) {
			return "Import částečně selhal.";
		}
		return "Import selhal.";
	};

	const getAlertSeverity = () => {
		if (result.success && !hasIssues) return "success";
		if (result.success && hasIssues) return "warning";
		return "error";
	};

	return (
		<Paper variant="outlined" sx={{ p: 3 }}>
			<Stack spacing={2}>
				<Typography variant="subtitle1" fontWeight="medium">
					Výsledek importu
				</Typography>

				<Alert
					severity={getAlertSeverity()}
					icon={
						result.success ? (
							hasIssues ? (
								<WarningIcon />
							) : (
								<CheckCircleIcon />
							)
						) : (
							<ErrorIcon />
						)
					}
				>
					<Typography variant="body2">{getStatusMessage()}</Typography>
				</Alert>

				<Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
					<Chip
						icon={<CheckCircleIcon />}
						label={`Zpracováno: ${result.definitionsProcessed}`}
						color="success"
						variant="outlined"
						size="small"
					/>
					{result.definitionsSkipped > 0 && (
						<Chip
							icon={<WarningIcon />}
							label={`Přeskočeno: ${result.definitionsSkipped}`}
							color="warning"
							variant="outlined"
							size="small"
						/>
					)}
					<Chip
						label={`Trvání: ${formatDuration(result.duration)}`}
						variant="outlined"
						size="small"
					/>
				</Stack>

				{result.cacheRefreshRequestId && (
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="caption" color="text.secondary">
							Cache refresh RequestID:
						</Typography>
						<Chip
							label={
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									<span style={{ fontFamily: "monospace" }}>
										{result.cacheRefreshRequestId}
									</span>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleCopyToClipboard(result.cacheRefreshRequestId!);
										}}
										sx={{
											padding: 0,
											"& .MuiSvgIcon-root": { fontSize: "0.75rem" },
										}}
									>
										<ContentCopyIcon />
									</IconButton>
								</Box>
							}
							size="small"
							variant="outlined"
							sx={{ fontSize: "0.75rem", height: 22 }}
						/>
					</Stack>
				)}

				{result.errors.length > 0 && (
					<Box
						sx={{
							bgcolor: "error.50",
							borderRadius: 1,
							overflow: "hidden",
						}}
					>
						<Typography
							variant="caption"
							fontWeight="medium"
							color="error.main"
							sx={{ px: 1.5, py: 0.75, display: "block" }}
						>
							Chyby ({result.errors.length})
						</Typography>
						<Box>
							{result.errors.map((error) => (
								<Accordion
									key={error}
									disableGutters
									elevation={0}
									sx={{
										bgcolor: "transparent",
										boxShadow: "none",
										"&:before": { display: "none" },
										"&.Mui-expanded": { margin: 0 },
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
										sx={{
											minHeight: 32,
											py: 0,
											px: 1.5,
											"& .MuiAccordionSummary-content": {
												my: 0.5,
												alignItems: "center",
												gap: 1,
											},
										}}
									>
										<ErrorIcon
											sx={{ fontSize: 14, color: "error.main", flexShrink: 0 }}
										/>
										<Typography
											variant="caption"
											color="error.main"
											sx={{
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{extractTitle(error)}
										</Typography>
									</AccordionSummary>
									<AccordionDetails sx={{ pt: 0, pb: 1, px: 1.5 }}>
										<Box
											sx={{
												bgcolor: "grey.50",
												borderRadius: 1,
												p: 1,
												maxHeight: 350,
												overflow: "auto",
											}}
										>
											<Typography
												variant="caption"
												sx={{
													fontFamily: "monospace",
													fontSize: 11,
													lineHeight: 1.4,
													whiteSpace: "pre-wrap",
													wordBreak: "break-word",
													color: "text.secondary",
												}}
											>
												{error}
											</Typography>
										</Box>
									</AccordionDetails>
								</Accordion>
							))}
						</Box>
					</Box>
				)}

				{result.warnings.length > 0 && (
					<Box
						sx={{
							bgcolor: "warning.50",
							borderRadius: 1,
							overflow: "hidden",
						}}
					>
						<Typography
							variant="caption"
							fontWeight="medium"
							color="warning.dark"
							sx={{ px: 1.5, py: 0.75, display: "block" }}
						>
							Varování ({result.warnings.length})
						</Typography>
						<Box>
							{result.warnings.map((warning) => (
								<Accordion
									key={warning}
									disableGutters
									elevation={0}
									sx={{
										bgcolor: "transparent",
										boxShadow: "none",
										"&:before": { display: "none" },
										"&.Mui-expanded": { margin: 0 },
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
										sx={{
											minHeight: 32,
											py: 0,
											px: 1.5,
											"& .MuiAccordionSummary-content": {
												my: 0.5,
												alignItems: "center",
												gap: 1,
											},
										}}
									>
										<WarningIcon
											sx={{
												fontSize: 14,
												color: "warning.main",
												flexShrink: 0,
											}}
										/>
										<Typography
											variant="caption"
											color="warning.dark"
											sx={{
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{extractTitle(warning)}
										</Typography>
									</AccordionSummary>
									<AccordionDetails sx={{ pt: 0, pb: 1, px: 1.5 }}>
										<Box
											sx={{
												bgcolor: "grey.50",
												borderRadius: 1,
												p: 1,
												maxHeight: 350,
												overflow: "auto",
											}}
										>
											<Typography
												variant="caption"
												sx={{
													fontFamily: "monospace",
													fontSize: 11,
													lineHeight: 1.4,
													whiteSpace: "pre-wrap",
													wordBreak: "break-word",
													color: "text.secondary",
												}}
											>
												{warning}
											</Typography>
										</Box>
									</AccordionDetails>
								</Accordion>
							))}
						</Box>
					</Box>
				)}

				{result.messages.length > 0 && (
					<Box>
						<Divider sx={{ my: 1 }} />
						<Typography variant="caption" fontWeight="medium" gutterBottom>
							Logy ({result.messages.length})
						</Typography>
						<Box
							sx={{
								maxHeight: 400,
								overflow: "auto",
								bgcolor: "grey.50",
								borderRadius: 1,
								p: 1,
								mt: 0.5,
							}}
						>
							{result.messages.map((message, index) => (
								<Box
									key={message}
									sx={{
										display: "flex",
										gap: 1.5,
										py: 0.125,
										userSelect: "none",
									}}
								>
									<Typography
										variant="caption"
										sx={{
											fontFamily: "monospace",
											fontSize: 11,
											lineHeight: 1.4,
											color: "text.disabled",
											minWidth: 32,
											textAlign: "right",
											flexShrink: 0,
										}}
									>
										{index + 1}
									</Typography>
									<Typography
										variant="caption"
										sx={{
											fontFamily: "monospace",
											fontSize: 11,
											lineHeight: 1.4,
											color: "text.secondary",
											userSelect: "text",
										}}
									>
										{message}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>
				)}
			</Stack>
		</Paper>
	);
};
